/**
 * Segment event payload builders for Native Interactive Site Map (ISM) tracking.
 *
 * Pure functions — no Redux, no DOM, no dispatch. Each returns a `{ eventName,
 * customProperties }` shape ready to spread into `segmentTrack(...)`. Property names match
 * Confluence page 5839192072 (footer comment 6000705547) exactly, including its existing
 * `lot_status_typeselected` / `previouslot_status_type` naming — these are not typos to fix.
 *
 * @module nativeInteractiveSiteMap/services/segmentEventBuilder
 */
import getTabTrackingValue from './tabTrackingValue';

/**
 * @param {object} params
 * @param {string} params.interactionType - 'zoom_in' | 'zoom_out' | 'pan' | 'map_type_changed'
 * @param {string} params.inputMethod - 'mouse' | 'touch' | 'button'
 * @param {string} [params.mapType] - 'road' | 'satellite' — only set for map_type_changed
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildMapInteractionEvent({ interactionType, inputMethod, mapType }) {
  const customProperties = {
    interaction_type: interactionType,
    input_method: inputMethod,
  };

  if (mapType) {
    customProperties.map_type = mapType;
  }

  return { eventName: 'native_map_interaction', customProperties };
}

/**
 * `lot_status_typeselected`/`previouslot_status_type` are only carried on the `lot_status_type`
 * (pill-click) path — a lot selection has no "previous chip" to report — so they're added
 * conditionally. `results_count`, `card_count`, `panel_type` and `source` are the core properties
 * and always present.
 *
 * @param {object} params
 * @param {string|null} params.selectedChip - the newly selected chip key (pill path only)
 * @param {string|null} params.previousChip - the chip key that was active before (pill path only)
 * @param {number} params.resultsCount - lot count for the current view
 * @param {number} params.cardCount - number of cards rendered in the active tab
 * @param {'lot_status_type'|'lot_selection'} params.source - what updated the panel
 * @param {boolean} params.isMobile
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildPanelUpdatedEvent({ selectedChip, previousChip, resultsCount, cardCount, source, isMobile }) {
  const customProperties = {
    results_count: resultsCount,
    card_count: cardCount,
    panel_type: isMobile ? 'sheet' : 'panel',
    source,
  };

  if (source === 'lot_status_type') {
    customProperties.lot_status_typeselected = selectedChip;
    customProperties.previouslot_status_type = previousChip;
  }

  return { eventName: 'native_map_panel_updated', customProperties };
}

/**
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildPanelScrollEvent() {
  return { eventName: 'native_map_panel_scroll', customProperties: {} };
}

/**
 * Card grid horizontal scroll — fires on both desktop and mobile, unlike buildPanelScrollEvent
 * (the lot-status pill bar), which is mobile-only.
 * @param {object} params
 * @param {boolean} params.isMobile
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildCardsScrolledEvent({ isMobile }) {
  return {
    eventName: 'home_card_scrolled',
    customProperties: { panel_type: isMobile ? 'sheet' : 'panel' },
  };
}

/**
 * @param {object} params
 * @param {string} params.selectedTab - internal tab constant (constants.js)
 * @param {string} params.previousTab - internal tab constant (constants.js)
 * @param {number} params.resultsCount
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildTabSelectedEvent({ selectedTab, previousTab, resultsCount }) {
  return {
    eventName: 'available_tab_selected',
    customProperties: {
      tab_selected: getTabTrackingValue(selectedTab),
      previous_tab: getTabTrackingValue(previousTab),
      results_count: resultsCount,
    },
  };
}

/**
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildNotifyWhenAvailableClickedEvent() {
  return { eventName: 'notify_when_available_clicked', customProperties: {} };
}

/**
 * Mobile drawer interaction — a vertical drag or the drawer being opened. No properties.
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildDrawerDraggedEvent() {
  return { eventName: 'native_map_sheet_dragged', customProperties: {} };
}

/**
 * @param {object} params
 * @param {object} params.lot - raw store lot object (see services/processLotMapInventory.js)
 * @param {number|null} params.planId
 * @param {number|null} params.specId
 * @param {number} params.plansCount
 * @param {boolean} params.isMobile
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildLotSelectedEvent({ lot, planId, specId, plansCount, isMobile }) {
  return {
    eventName: isMobile ? 'native_map_lot_selected_m' : 'native_map_lot_selected_d',
    customProperties: {
      plan_id: planId,
      spec_id: specId,
      lot_id: lot.lotId,
      lot_number: lot.displayLabel,
      lot_group: lot.lotGroupName,
      lot_status: lot.status,
      plans_count: plansCount,
      lot_type: lot.lotType,
      lot_size: lot.lotSize,
      lot_facing: lot.lotFacing,
    },
  };
}
